import { EnumTranslatorBase, ImportDuplicates, LabeledEnumValue, LabeledEnumValueConverter } from '@skylab-shared/dataModel';

import { ImportRepositoryBase } from '@skylab-angular-shared/business';
import { DialogContentComponentBase, DialogInsService } from '@skylab-angular-shared/ui';

export interface ImportViewModel {
    uploadedFile?: File;
    onFilePick: (file: File) => void;
    onFileClear: () => void;
    passwordVisible: () => boolean;
    passwordWarningVisible: () => boolean;
    password: string;
    handleDuplicate: ImportDuplicates;
    handleDuplicates: LabeledEnumValue[];
    handleDuplicatesValueConverter: LabeledEnumValueConverter<ImportDuplicates>;
}

export abstract class ImportDialogBase<TViewModel extends ImportViewModel> extends DialogContentComponentBase<TViewModel> {
    public constructor(
        protected readonly enumTranslator: EnumTranslatorBase,
        protected readonly importRepository: ImportRepositoryBase,
        protected override readonly dialogService: DialogInsService
    ) {
        super(dialogService);
    }

    protected override getInitialState(): TViewModel {
        return <TViewModel>{
            onFilePick: (file: File) => {
                this.viewModel.uploadedFile = file;
            },
            onFileClear: () => {
                this.viewModel.uploadedFile = undefined;
            },
            passwordVisible: () => !!this.viewModel.uploadedFile && this.getFileExtension(this.viewModel.uploadedFile) === 'zip',
            passwordWarningVisible: () => this.showPasswordWarning,
            password: '',
            handleDuplicate: ImportDuplicates.Overwrite,
            handleDuplicates: this.enumTranslator.getLabeledEnumValueArray(ImportDuplicates),
            handleDuplicatesValueConverter: new LabeledEnumValueConverter(this.enumTranslator, ImportDuplicates)
        };
    }

    protected abstract postImportRequestAsync(data: FormData): Promise<any>;
    protected abstract onImportSuccess(response: any): void;
    protected abstract onImportError(reason: any): void;

    public showPasswordWarning!: boolean;
    public isImporting!: boolean;
    public async onImportAsync(): Promise<any> {
        if (this.isImporting) return;

        this.isImporting = true;
        this.showPasswordWarning = false;
        try {
            const file = this.getSourceFile();

            if (file) {
                const ext = this.getFileExtension(file);
                const fd = new FormData();
                fd.append(file.name, file);

                if (ext === 'zip' && this.viewModel.password) {
                    fd.append('password', this.viewModel.password);
                }

                const response = await this.postImportRequestAsync(fd);
                if (response.isSuccessfull) {
                    this.onImportSuccess(response);
                } else {
                    this.onImportError(response);
                }
            }
        } catch (reason) {
            this.logger.error(reason);
            const errorResponse = reason as any;
            if (errorResponse && errorResponse.data && errorResponse.data.code === 'WrongArchivePassword') {
                this.showPasswordWarning = true;
            }
        } finally {
            this.isImporting = false;
        }
    }

    private getSourceFile(): File | undefined {
        return this.viewModel.uploadedFile;
    }

    private getFileExtension(file: File): string {
        const filename = file.name;
        return filename.substr(filename.lastIndexOf('.') + 1);
    }

    public onPasswordChange() {
        this.showPasswordWarning = false;
    }
}
